import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { NotebookDocumentResponse } from '@lp/shared';
import { Download } from 'lucide-react';
import { api, ApiError } from '../../lib/api.js';
import { Markdown } from '../Markdown.js';
import { Alert, Button, LoadingPane } from '../ui.js';

/**
 * A notebook shown as it was saved: markdown cells rendered, code cells with
 * their stored outputs. No kernel and nothing executed — this is the reading
 * view, and the fallback when a server has no JupyterLite to run one.
 *
 * Outputs are author content and are treated as untrusted. Text, images and
 * markdown render in the page (markdown through the sanitizing renderer);
 * HTML outputs (a pandas table, say) go into a frame sandboxed with no
 * scripts at all, so a `<script>` in an output is inert.
 */

type Cell = {
  cell_type?: string;
  source?: string | string[];
  execution_count?: number | null;
  outputs?: Output[];
};

type Output = {
  output_type?: string;
  name?: string;
  text?: string | string[];
  data?: Record<string, string | string[] | undefined>;
  ename?: string;
  evalue?: string;
  traceback?: string[];
};

const joined = (v: string | string[] | undefined): string => (Array.isArray(v) ? v.join('') : (v ?? ''));
const ANSI = /\u001b\[[0-9;]*[A-Za-z]/g;

export function useNotebookDocument(featureId: string) {
  return useQuery({
    queryKey: ['notebook-document', featureId],
    queryFn: ({ signal }) =>
      api.get<NotebookDocumentResponse>(`/interactive/${encodeURIComponent(featureId)}/notebook`, signal),
    staleTime: 5 * 60_000,
    retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
  });
}

/** Save the notebook as the author uploaded it. */
export function downloadNotebook(doc: NotebookDocumentResponse): void {
  const blob = new Blob([JSON.stringify(doc.notebook, null, 1)], { type: 'application/x-ipynb+json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = doc.fileName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

export function NotebookViewer({ featureId, maxHeightPx }: { featureId: string; maxHeightPx?: number }) {
  const doc = useNotebookDocument(featureId);

  if (doc.isPending) return <LoadingPane label="Loading the notebook…" />;
  if (doc.isError) {
    return (
      <Alert tone="warn" title="The notebook could not be loaded">
        {doc.error instanceof ApiError ? doc.error.message : 'Something went wrong while loading it.'}
      </Alert>
    );
  }

  const cells = Array.isArray(doc.data.notebook.cells) ? (doc.data.notebook.cells as Cell[]) : [];

  return (
    <div className="flex flex-col gap-3">
      <div
        className="flex flex-col gap-4 overflow-auto rounded-control border border-line bg-surface p-4"
        style={maxHeightPx ? { maxHeight: `${maxHeightPx}px` } : undefined}
      >
        {cells.length ? (
          cells.map((cell, i) => <NotebookCell key={i} cell={cell} />)
        ) : (
          <p className="text-sm text-ink-muted">This notebook has no cells.</p>
        )}
      </div>
      <div>
        <Button
          size="sm"
          variant="ghost"
          icon={<Download size={14} aria-hidden />}
          onClick={() => downloadNotebook(doc.data)}
        >
          Download {doc.data.fileName}
        </Button>
      </div>
    </div>
  );
}

function NotebookCell({ cell }: { cell: Cell }) {
  const source = joined(cell.source);
  if (cell.cell_type === 'markdown') return <Markdown>{source}</Markdown>;
  if (cell.cell_type === 'raw') {
    return <pre className="whitespace-pre-wrap font-mono text-xs text-ink-muted">{source}</pre>;
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-3">
        <span className="w-12 shrink-0 pt-2 text-right font-mono text-xs text-ink-subtle" aria-hidden>
          [{cell.execution_count ?? ' '}]
        </span>
        <pre className="min-w-0 flex-1 overflow-x-auto rounded-control bg-surface-2 p-3 font-mono text-xs leading-relaxed text-ink">
          <span className="sr-only">Code: </span>
          {source}
        </pre>
      </div>
      {cell.outputs?.length ? (
        <div className="ml-15 flex min-w-0 flex-col gap-2">
          {cell.outputs.map((output, i) => (
            <NotebookOutput key={i} output={output} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function NotebookOutput({ output }: { output: Output }) {
  if (output.output_type === 'stream') {
    return (
      <pre
        className={
          output.name === 'stderr'
            ? 'overflow-x-auto whitespace-pre-wrap rounded-control bg-warn-wash p-2 font-mono text-xs text-warn'
            : 'overflow-x-auto whitespace-pre-wrap font-mono text-xs text-ink'
        }
      >
        {joined(output.text)}
      </pre>
    );
  }

  if (output.output_type === 'error') {
    return (
      <pre className="overflow-x-auto whitespace-pre-wrap rounded-control bg-danger-wash p-2 font-mono text-xs text-danger">
        {(output.traceback?.length
          ? output.traceback.join('\n')
          : `${output.ename ?? 'Error'}: ${output.evalue ?? ''}`
        ).replace(ANSI, '')}
      </pre>
    );
  }

  const data = output.data ?? {};
  const png = joined(data['image/png']);
  if (png) return <img alt="Cell output" className="max-w-full" src={`data:image/png;base64,${png.replace(/\s/g, '')}`} />;
  const jpeg = joined(data['image/jpeg']);
  if (jpeg) return <img alt="Cell output" className="max-w-full" src={`data:image/jpeg;base64,${jpeg.replace(/\s/g, '')}`} />;
  const svg = joined(data['image/svg+xml']);
  // As an <img>, an SVG cannot run script.
  if (svg) return <img alt="Cell output" className="max-w-full" src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`} />;
  const html = joined(data['text/html']);
  if (html) return <HtmlOutput html={html} />;
  const md = joined(data['text/markdown']);
  if (md) return <Markdown compact>{md}</Markdown>;
  const text = joined(data['text/plain']);
  if (text) return <pre className="overflow-x-auto whitespace-pre-wrap font-mono text-xs text-ink">{text.replace(ANSI, '')}</pre>;
  return null;
}

/**
 * An HTML output in a frame with `sandbox="allow-same-origin"` and nothing
 * else: no scripts run, no forms submit, nothing navigates. Same-origin is what
 * lets this page measure the content to size the frame — with scripts off, the
 * output cannot use it for anything. The page's own text colours are passed in
 * so the output follows the current theme.
 */
function HtmlOutput({ html }: { html: string }) {
  const ref = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(60);
  const [colors, setColors] = useState({ ink: 'inherit', line: 'currentColor' });

  useEffect(() => {
    const style = getComputedStyle(document.body);
    setColors({ ink: style.color, line: style.borderColor || style.color });
  }, []);

  const doc = `<!doctype html><html><head><meta charset="utf-8"><style>
    html,body{margin:0;background:transparent;color:${colors.ink};font:12px/1.45 ui-sans-serif,system-ui,sans-serif}
    table{border-collapse:collapse}th,td{padding:4px 8px;border-bottom:1px solid ${colors.line};text-align:right}
    th{font-weight:600}img{max-width:100%}
  </style></head><body>${html}</body></html>`;

  return (
    <iframe
      ref={ref}
      title="Cell output"
      sandbox="allow-same-origin"
      srcDoc={doc}
      referrerPolicy="no-referrer"
      className="w-full rounded-control border-0"
      style={{ height: `${Math.min(height, 800)}px` }}
      onLoad={() => {
        const body = ref.current?.contentDocument?.body;
        if (body) setHeight(body.scrollHeight + 8);
      }}
    />
  );
}
